import { css } from "@emotion/react";
import { motion } from "framer-motion";
import {
	ChevronDownIcon,
	LayoutGridIcon,
	ListIcon,
	MenuIcon,
} from "lucide-react";
import type React from "react";
import { useEffect } from "react";
import { BreadCrumbs } from "../../components/ProductBrowser/BreadCrumbs";
import { useCatalogState } from "../../hooks/useCatalogState";
import { useCheckoutFormState } from "../../hooks/useCheckoutFormState";
import type { FwQueryState } from "../../hooks/useFwQuery";
import type { Product } from "../../hooks/useInventoryState";
import { useInventoryState } from "../../hooks/useInventoryState";
import { useProductBrowserState } from "../../hooks/useProductBrowserState";
import { useSessionState } from "../../hooks/useSessionState";
import { useStorefrontSettingsState } from "../../hooks/useStorefrontSettingsState";
import { useTailwindMediaQueries } from "../../hooks/useTailwindMediaQueries";
import { useThemeState } from "../../hooks/useThemeState";
import { useTranslate } from "../../hooks/useTranslate/useTranslate";
import { CatalogMenu } from "../CatalogMenu";
import { ReservationDates } from "../CheckoutForm/ReservationDates";
import { SelectLocation } from "../CheckoutForm/SelectLocation";
import { CustomScrollbars } from "../CustomScrollbars/CustomScrollbars";
import { LoadingDots } from "../LoadingDots";
import { Button } from "../ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { ProductBrowserGrid } from "./GridView/ProductBrowserGrid";
import { ItemWidthSlider } from "./ItemWidthSlider";
import { LandingPage } from "./LandingPage";
import { ProductBrowserList } from "./ListView/ProductBrowserList";
import { ProductSearchBox } from "./ProductSearchBox";
import { Sidebar } from "./Sidebar/Sidebar";

export type ProductBrowserProps = {
	catalogid: string | undefined;
	query?: FwQueryState<Product>;
};
export const ProductBrowser: React.FC<ProductBrowserProps> = (props) => {
	const inventoryState = useInventoryState();
	// Use the query prop if provided, otherwise fall back to the old system
	const products = props.query
		? {
				Items: props.query.items,
				PageNo: props.query.pageNo,
				PageSize: props.query.pageSize,
				TotalItems: props.query.totalItems,
				Sort: props.query.sort,
			}
		: inventoryState.getProductsState().get().data;
	const productBrowserState = useProductBrowserState();
	const view = productBrowserState.get().view;
	//const isDesktop = useIsDesktopMediaQuery();
	const sidebarExpanded = inventoryState.get().sidebarExpanded;
	const themeState = useThemeState();
	const theme = themeState.get().theme;
	const isDarkTheme = themeState.isDark();
	const translate = useTranslate();
	const checkoutFormState = useCheckoutFormState();
	const showLandingPage = inventoryState.get().showLandingPage;
	const catalogState = useCatalogState();

	const sessionState = useSessionState();
	const loggedIn = sessionState.get().loggedIn;
	const storefrontSettingsState = useStorefrontSettingsState();
	const settings = storefrontSettingsState.get().settings;
	const mediaQueries = useTailwindMediaQueries();

	const separateQuoteRequestsByCatalog =
		settings?.SeparateQuoteRequestsByCatalog ?? false;
	const mobileHeaderLocations = checkoutFormState.get().lookuplocation ?? [];
	const currentLocation = checkoutFormState.getLocation();

	// const fromDate = dayjs(checkoutFormState.get().fromDateValue).format('ddd MMM YY, YYYY');
	// const fromTime = dayjs(checkoutFormState.get().fromDateValue).format('LT');
	// const toDate = dayjs(checkoutFormState.get().toDateValue).format('ddd MMM YY, YYYY');
	// const toTime = dayjs(checkoutFormState.get().toDateValue).format('LT');

	// Smart loading logic - show loading dots when data is being fetched
	const shouldShowLoading = () => {
		const isLoading = props.query
			? props.query.isPending
			: inventoryState.getProductsState().get().isLoading;

		// Always show loading when data is being fetched, regardless of browser
		return isLoading;
	};

	useEffect(() => {
		if (
			settings &&
			(settings.IsAnonymousAccessEnabled ||
				(!settings.IsAnonymousAccessEnabled && loggedIn))
		) {
			const currentCatalog = inventoryState.get().catalog;
			const currentWarehouse = checkoutFormState.getWarehouse();
			const currentLocation = checkoutFormState.getLocation();
			inventoryState.setProductFilterAsync({
				catalog: currentCatalog,
				warehouse: currentWarehouse,
				location: currentLocation,
				showLandingPage: showLandingPage,
			});
		}
	}, [
		loggedIn,
		settings,
		showLandingPage,
		inventoryState.get,
		checkoutFormState.getWarehouse,
		checkoutFormState.getLocation,
		inventoryState.setProductFilterAsync,
	]);

	return (
		<div
			css={css`
                flex: 1 1 0;
                display:flex;
                flex-direction:column;
                overflow: hidden;
                position: relative;`}
		>
			{shouldShowLoading() && (
				<div
					css={css`
                        position:fixed;
                        top:0;
                        right:0;
                        bottom:0;
                        left:0;
                        /*background-color:#FFFFFF55;*/
                        z-index:4;
                        display:flex;
                        align-items:center;
                        justify-content:center;`}
				>
					<LoadingDots />
				</div>
			)}
			<div
				css={css`
                        flex: 1 1 0;
                        display: flex;
                        flex-direction: column;
                        overflow:hidden;`}
			>
				<div css={css`flex: 1 1 0;display:flex;`}>
					{props.catalogid && mediaQueries.minw_md && (
						<motion.div
							css={css`
                                    flex:0 0 auto;
                                    ${sidebarExpanded ? "width: 320px;" : ""}
                                    display:flex;
                                    flex-direction: column;
                                    transition:background-color 1s;
                                    font-size: .8em;
                                    overflow:hidden;
                                `}
							style={{
								backgroundColor: theme.palette.siteSideBar,
							}}
							transition={{ type: "spring", stiffness: 200, damping: 22 }}
						>
							<Sidebar />
						</motion.div>
					)}
					<div
						css={css`
                                flex:1 1 0;
                                display:flex;
                                flex-wrap: wrap;
                                flex-direction: column;
                            `}
					>
						{/* less than 768 (phone) */}
						{mediaQueries.maxw_sm && (
							<div
								css={css`display:flex;align-items:flex-start;flex-wrap:wrap;`}
							>
								<Tooltip>
									<TooltipTrigger
										render={
											<Button
												variant="ghost"
												size="icon"
												className="size-8 m-2"
												aria-label={translate("Search Options")}
												onClick={async (_e) => {
													return Promise.resolve().then(() => {
														inventoryState.setSidebarExpanded(true);
													});
												}}
											>
												<MenuIcon className="size-5.5" />
											</Button>
										}
									/>
									<TooltipContent>{translate("Search Options")}</TooltipContent>
								</Tooltip>
								<div
									css={css`flex: 1 1 0;font-size:.8em;white-space:nowrap;cursor:pointer;`}
								>
									<div className="flex flex-wrap">
										{(inventoryState.get().catalog?.HasRental ?? false) && (
											<ReservationDates
												className="mr-4"
												catalogId={inventoryState.get().catalog?.Id}
											/>
										)}
										<div className="flex items-center justify-center p-2">
											{!separateQuoteRequestsByCatalog && (
												<div
													css={css`
														display:flex;
														align-items:center;
													`}
												>
													<span
														css={css`
															padding:0 0.1rem 0 0;
															color:${themeState.isDark() ? "#ccc" : "#777"};
														`}
													>
														{translate("Location")}:
													</span>
													<DropdownMenu>
														<DropdownMenuTrigger
															render={
																<Button
																	variant="link"
																	size="xs"
																	css={css`
																		margin: 0 0 0 .35rem;
																		padding: 0;
																		font-size:.8rem;
																		display:flex;
																		align-items:center;
																		gap:.1rem;
																		cursor: pointer;
																		text-decoration: underline;
																		text-underline-offset: 2px;
																		height:auto;
																	`}
																>
																	{currentLocation?.Location ?? ""}
																	<ChevronDownIcon size={14} />
																</Button>
															}
														/>
														<DropdownMenuContent
															align="start"
															className="w-auto min-w-40"
														>
															{mobileHeaderLocations.map((location) => {
																let locationName = location.Location;
																if (location.Warehouse !== location.Location) {
																	locationName += ` (${location.Warehouse})`;
																}
																return (
																	<DropdownMenuItem
																		key={location.LocationId}
																		onClick={() => {
																			checkoutFormState.setLocationId(
																				location.LocationId,
																			);
																			if (location.WarehouseId) {
																				checkoutFormState.setWarehouseId(
																					location.WarehouseId,
																				);
																			}
																		}}
																	>
																		{locationName}
																	</DropdownMenuItem>
																);
															})}
														</DropdownMenuContent>
													</DropdownMenu>
												</div>
											)}
											<div
												css={css`display:flex;align-items:center;padding: 0 .5rem 0 0;`}
											>
												<CatalogMenu />
											</div>
										</div>
									</div>
								</div>
								<div css={css`flex:1 1 0;display: flex;padding:.5rem .5rem`}>
									<ProductSearchBox />
								</div>
							</div>
						)}

						{/* less than 1280 (tablet) */}
						{mediaQueries.tablet && (
							<div css={css`padding:.4rem 0 0 .4rem;display:flex;`}>
								{/* <div css={css`flex: 1 1 auto;display:flex;flex:0 0 auto;align-items:center;
                                    justify-content:flex-end;`}>
                                    <div css={css`flex:0 0 auto;`}
                                        onClick={async e => {
                                            return Promise.resolve().then(() => {
                                                inventoryState.setSidebarExpanded(true);
                                            })
                                        }}>
                                        <Tooltip title={translate('Search Options')}>
                                            <IconButton avria-label={translate('Search Options')} size="large">
                                                <Menu css={css`font-size:32px;`} fontSize="large" />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                </div> */}
								<div
									css={css`flex:1 1 0;display:flex;align-items:center;flex-wrap:wrap;`}
								>
									{!separateQuoteRequestsByCatalog &&
										!inventoryState.get().sidebarExpanded && (
											<div css={css`padding:0 5px 0 .5rem;flex:1 1 0;`}>
												<SelectLocation
													disabled={false}
													css={css`flex:1 1 0;`}
												/>
											</div>
										)}
									<div css={css`flex: 0 0 auto;padding:8px 0 8px .5rem;`}>
										<ReservationDates
											catalogId={inventoryState.get().catalog?.Id}
										/>
									</div>
									{/* <span css={css`flex: 0 0 auto;padding:0 .5rem;`}>-</span>
                                    <div css={css`padding:8px 0 8px .5rem;`}>
                                        <CheckoutDate type="stopdate" catalogId={inventoryState.get().catalog?.Id} />
                                    </div> */}
									{/* <span className="text-xs" css={css`flex: 0 0 auto;padding:0 .5rem;`}>({durationDays} {translate('days')})</span> */}
									<div css={css`flex:1 1 0;display: flex;padding:.5rem .5rem`}>
										<ProductSearchBox />
									</div>
								</div>
							</div>
						)}

						{props.catalogid && (
							<div
								css={css`
                                flex: 0 0 auto;
                                padding: 2px 0;
                                overflow: hidden;
                                display: flex;
                                flex-wrap: wrap;`}
							>
								{mediaQueries.minw_xl && (
									<div css={css`flex:1 1 0;display: flex;padding:.5rem 0;`}>
										<ProductSearchBox />
									</div>
								)}
								<div
									css={css`
                                    flex: 0 0 auto;
                                    align-self: flex-end;
                                    padding:.25rem .75rem;
                                    display:flex;
                                    align-items:center;
                                    @media (min-width: 720px) {
                                        flex: 0 0 auto;
                                    }
                                    `}
								>
									{mediaQueries.minw_xl && !showLandingPage && (
										<>
											<ItemWidthSlider />
											<div className="inline-flex items-center gap-1 rounded-md border border-input p-1">
												<Tooltip>
													<TooltipTrigger
														render={
															<Button
																variant={
																	productBrowserState.get().view === "list"
																		? "secondary"
																		: "ghost"
																}
																size="icon-sm"
																aria-label="list"
																onClick={async () => {
																	return Promise.resolve().then(() => {
																		productBrowserState.setView("list");
																	});
																}}
															>
																<ListIcon size={16} />
															</Button>
														}
													/>
													<TooltipContent>
														{translate("List View")}
													</TooltipContent>
												</Tooltip>
												<Tooltip>
													<TooltipTrigger
														render={
															<Button
																variant={
																	productBrowserState.get().view === "grid"
																		? "secondary"
																		: "ghost"
																}
																size="icon-sm"
																aria-label="grid"
																onClick={async () => {
																	return Promise.resolve().then(() => {
																		productBrowserState.setView("grid");
																	});
																}}
															>
																<LayoutGridIcon size={16} />
															</Button>
														}
													/>
													<TooltipContent>
														{translate("Grid View")}
													</TooltipContent>
												</Tooltip>
											</div>
										</>
									)}
									{/* {(productBrowserState.get().view === 'grid' || !mediaQueries.minw_xl) && (
                                        <ToggleButtonGroup
                                            css={css`padding:0 .25rem;`}
                                            orientation="horizontal"
                                            value={productBrowserState.get().view}
                                            exclusive
                                            onChange={async (e, value) => {
                                                return Promise.resolve().then(() => {
                                                    if (value) {
                                                        productBrowserState.setExpandedView(value);
                                                    }
                                                });
                                            }}>
                                            {productBrowserState.get().expandedView && (
                                                <ToggleButton value={false}>
                                                    <Tooltip title={translate('Collapse')}>
                                                        <UnfoldLess onClick={e => productBrowserState.setExpandedView(false)} />
                                                    </Tooltip>
                                                </ToggleButton>
                                            )}
                                            {!productBrowserState.get().expandedView && (
                                                <ToggleButton value={true}>
                                                    <Tooltip title={translate('Expand')} >
                                                        <UnfoldMore onClick={e => productBrowserState.setExpandedView(true)} />
                                                    </Tooltip>
                                                </ToggleButton>
                                            )}
                                        </ToggleButtonGroup>
                                    )} */}
								</div>
							</div>
						)}

						{props.catalogid && mediaQueries.phone && sidebarExpanded && (
							<motion.div
								css={css`
                                    position:fixed;
                                    top:0;
                                    right: ${mediaQueries.phone ? "0" : "unset"};
                                    bottom:0;
                                    left:0;
                                    z-index:4;
                                    flex:0 0 auto;
                                    display:flex;
                                    flex-direction: column;
                                    transition:background-color 1s;
                                    font-size: .9em;
                                    overflow:hidden;
                                    box-shadow: 0 5px 5px rgba(182, 182, 182, 0.75);
                                `}
								style={{
									backgroundColor: isDarkTheme ? "#333333EE" : "#ffffffEE",
								}}
								transition={{ type: "spring", stiffness: 200, damping: 22 }}
							>
								<Sidebar />
							</motion.div>
						)}

						<div
							css={css`flex:0 0 auto;padding:.25em 0;display:flex;align-items:center;justify-content:center;`}
						>
							<BreadCrumbs></BreadCrumbs>
						</div>

						{showLandingPage && (
							<div css={css`flex:1 1 0;overflow:auto;`}>
								<div css={css`margin: 0 auto;`}>
									{catalogState.get().catalogs?.map((catalog) => {
										if (!props.catalogid || props.catalogid === catalog.Id) {
											return (
												<LandingPage
													key={props.catalogid}
													catalog={catalog}
													showCatalogName={props.catalogid !== catalog.Id}
													showCategoryTreeGrid={true}
												/>
											);
										}
										return null;
									})}
								</div>
							</div>
						)}

						{!showLandingPage && products && (
							<div
								css={css`
                                        flex: 1 1 0;
                                        display: flex;
                                        flex-direction: column;
                                        position: relative;
                                        padding: 0 0px 0 0;
                                    `}
							>
								<CustomScrollbars
									css={css`flex:1 1 0;display:flex;flex-direction:column;`}
									data-testid="product-browser-scroll"
								>
									{products.Items.length === 0 && !shouldShowLoading() ? (
										<div
											css={css`
                                            display: flex;
                                            flex-direction: column;
                                            align-items: center;
                                            justify-content: center;
                                            padding: 2rem;
                                            text-align: center;
                                            color: ${themeState.isDark() ? "#ccc" : "#666"};
                                        `}
										>
											<div css={css`font-size: 1.2rem; margin-bottom: 0.5rem;`}>
												{translate("No products found")}
											</div>
											<div css={css`font-size: 0.9rem;`}>
												{translate(
													"Try adjusting your search criteria or browse different categories",
												)}
											</div>
										</div>
									) : (
										<>
											{(!mediaQueries.minw_xl || view === "grid") && (
												<ProductBrowserGrid products={products} />
											)}
											{mediaQueries.minw_xl && view === "list" && (
												<ProductBrowserList products={products} />
											)}
										</>
									)}
								</CustomScrollbars>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
